"use client";

import { APP_NAME } from "@calcom/lib/constants";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import classNames from "@calcom/ui/classNames";
import { Icon } from "@calcom/ui/components/icon";
import type { ReactNode } from "react";

const FEATURES = [
  {
    title: "connect_all_calendars",
    description: "connect_all_calendars_description",
    i18nOptions: {
      appName: APP_NAME,
    },
    icon: "calendar-heart" as const,
  },
  {
    title: "set_availability",
    description: "set_availbility_description",
    icon: "users" as const,
  },
  {
    title: "share_a_link_or_embed",
    description: "share_a_link_or_embed_description",
    icon: "link-2" as const,
    i18nOptions: {
      appName: APP_NAME,
    },
  },
];

type AuthSplitShellProps = {
  children: ReactNode;
};

export function AuthSplitShell({ children }: AuthSplitShellProps) {
  const { t } = useLocale();

  return (
    <div
      className={classNames(
        "light flex min-h-screen w-full flex-col items-center justify-center bg-cal-muted [--cal-brand:#111827] 2xl:bg-default dark:[--cal-brand:#FFFFFF]",
        "[--cal-brand-subtle:#9CA3AF]",
        "[--cal-brand-text:#FFFFFF] dark:[--cal-brand-text:#000000]",
        "[--cal-brand-emphasis:#101010] dark:[--cal-brand-emphasis:#e1e1e1]"
      )}>
      <div className="grid w-full max-w-[1440px] grid-cols-1 grid-rows-1 overflow-hidden bg-cal-muted lg:grid-cols-2 2xl:rounded-[20px] 2xl:border 2xl:border-subtle 2xl:py-6">
        <div className="mt-0 mr-auto ml-auto flex w-full max-w-xl flex-col px-4 pt-6 sm:px-16 md:px-20 lg:mt-24 2xl:px-28">
          {children}
        </div>
        <div className="mx-auto mt-24 w-full max-w-2xl flex-col justify-between rounded-l-2xl border-subtle pl-4 lg:mt-0 lg:flex lg:max-w-full lg:border lg:bg-subtle lg:py-12 lg:pl-12 dark:bg-none">
          <div className="hidden rounded-tl-2xl rounded-br-none rounded-bl-2xl border border-default border-r-0 border-dashed bg-black/3 lg:block lg:py-[6px] lg:pl-[6px] dark:bg-white/5">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              className="block dark:hidden"
              src="/mock-event-type-list.svg"
              alt={`${APP_NAME} Booking Page`}
            />
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              className="hidden dark:block"
              src="/mock-event-type-list-dark.svg"
              alt={`${APP_NAME} Booking Page`}
            />
          </div>
          <div className="mt-8 mr-12 hidden h-full w-full grid-cols-3 gap-4 overflow-hidden lg:grid">
            {FEATURES.map((feature, index) => (
              <div key={index} className="mb-8 flex max-w-52 flex-col leading-none sm:mb-0">
                <div className="items-center text-emphasis">
                  <Icon name={feature.icon} className="mb-1 h-4 w-4" />
                  <span className="font-medium text-sm">{t(feature.title)}</span>
                </div>
                <div className="text-sm text-subtle">
                  <p>
                    {t(
                      feature.description,
                      feature.i18nOptions && {
                        ...feature.i18nOptions,
                      }
                    )}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
